/**
 * Height Animator
 *
 * GSAP-based height animations with singleton pattern.
 * Supports customizable duration, easing, and respects prefers-reduced-motion.
 */
import config from '@config';
import { getGsap, prefersReducedMotion, type GsapTween } from './gsap.ts';

interface AnimateOptions {
  duration?: number;
  ease?: string;
}

class HeightAnimator {
  #activeAnimations = new Map<HTMLElement, GsapTween>();

  /**
   * Animate element height to open or closed state
   */
  async animate(
    element: HTMLElement,
    isOpen: boolean,
    options: AnimateOptions = {}
  ): Promise<void> {
    const gsapLib = getGsap('heightAnimator');
    if (!gsapLib) return;

    const { gsap } = gsapLib;

    const heightAttr = config._global.attributes.height;
    const heightValue = config._global.attributes.heightValue;
    const durationAttr = config._global.attributes.heightDuration;
    const easeAttr = config._global.attributes.heightEase;

    // Check for custom height target element
    const heightTarget =
      element.hasAttribute?.(heightAttr) && element.getAttribute(heightAttr) === heightValue
        ? element
        : (element.querySelector?.(`[${heightAttr}="${heightValue}"]`) as HTMLElement) || element;

    // Cancel existing animation on this element
    const existingAnim = this.#activeAnimations.get(heightTarget);
    if (existingAnim) {
      existingAnim.kill();
      this.#activeAnimations.delete(heightTarget);
    }

    // Get attributes - check both the target AND its parent wrapper
    let durationValue = heightTarget.getAttribute(durationAttr);
    let easeValue = heightTarget.getAttribute(easeAttr);

    // If not found on target, check parent wrapper
    if (!durationValue && heightTarget.parentElement) {
      durationValue = heightTarget.parentElement.getAttribute(durationAttr);
    }
    if (!easeValue && heightTarget.parentElement) {
      easeValue = heightTarget.parentElement.getAttribute(easeAttr);
    }

    // Fallback chain: attribute > options > default
    let duration = durationValue
      ? parseInt(durationValue, 10) / 1000
      : (options.duration || 300) / 1000;

    // Force instant updates if user prefers reduced motion
    if (prefersReducedMotion()) {
      duration = 0;
    }

    const ease = easeValue || options.ease || 'power2.inOut';

    // Create animation
    const animation = gsap.to(heightTarget, {
      height: isOpen ? 'auto' : 0,
      duration: duration,
      ease: ease,
    });

    // Track active animation
    this.#activeAnimations.set(heightTarget, animation);

    // Wait for animation to complete
    try {
      await animation.then();
    } finally {
      this.#activeAnimations.delete(heightTarget);
    }
  }

  /**
   * Set element height immediately without animation (for initial state)
   */
  setHeight(element: HTMLElement, isOpen: boolean): void {
    const gsapLib = getGsap('heightAnimator');
    if (!gsapLib) return;

    const { gsap } = gsapLib;

    const heightAttr = config._global.attributes.height;
    const heightValue = config._global.attributes.heightValue;

    // Check for custom height target element
    const heightTarget =
      element.hasAttribute?.(heightAttr) && element.getAttribute(heightAttr) === heightValue
        ? element
        : (element.querySelector?.(`[${heightAttr}="${heightValue}"]`) as HTMLElement) || element;

    // Set immediately without animation
    gsap.set(heightTarget, {
      height: isOpen ? 'auto' : 0,
    });
  }

  /**
   * Cancel all active animations (for cleanup)
   */
  cancelAll(): void {
    this.#activeAnimations.forEach((anim) => anim.kill());
    this.#activeAnimations.clear();
  }
}

// Export singleton instance
export const heightAnimator = new HeightAnimator();

// Export bound methods for convenience
export const animateHeight = heightAnimator.animate.bind(heightAnimator);
export const setHeight = heightAnimator.setHeight.bind(heightAnimator);
